import React, { useCallback, useMemo } from 'react'
import { Box, List, ListItem, Paper, Typography } from '@material-ui/core'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'
import _ from 'lodash'
import { RuleStepProps } from '../../CampaignModalSkeleton/CampaignModalSkeleton.types'
import IconButton from '../../../IconButton'
import Button from '../../../Button'
import {
	handleProductToggle,
	handleAddProducts,
	handleDelete,
} from '../../MarketingCampaigns.promo.services'
import { BogoSameRuleState } from './types'
import { ProductScopeSelector } from '../../CampaignModalSkeleton/components'
import { useCampaignModalSkeletonStyles } from '../../CampaignModalSkeleton/CampaignModalSkeleton.styles'

const WITH_BUTTON = true

export function BogoSameRuleStep({
	rule,
	setRule,
	description,
	sections,
}: RuleStepProps<BogoSameRuleState>) {
	const classes = useCampaignModalSkeletonStyles()
	const setPurchasedProducts = useCallback(
		(updater: any) => {
			setRule((r) => ({
				...r,
				purchasedProducts:
					typeof updater === 'function' ? updater(r.purchasedProducts) : updater,
			}))
		},
		[setRule],
	)

	const setSelectedProducts = useCallback(
		(updater: any) => {
			setRule((r) => ({
				...r,
				selectedProducts:
					typeof updater === 'function' ? updater(r.selectedProducts) : updater,
			}))
		},
		[setRule],
	)

	const setFormValuesAdapter = useCallback(
		(updater: any) => {
			setRule((r) => {
				const patch = typeof updater === 'function' ? updater(r) : updater
				return { ...r, ...patch }
			})
		},
		[setRule],
	)

	const setDisabledPurchased = useCallback(
		(updater: any) => {
			setRule((r) => ({
				...r,
				disabledPurshasedProducts:
					typeof updater === 'function'
						? updater(r.disabledPurshasedProducts)
						: updater,
			}))
		},
		[setRule],
	)

	const setOfferedProducts = useCallback(
		(updater: any) => {
			setRule((r) => ({
				...r,
				offeredProducts:
					typeof updater === 'function' ? updater(r.offeredProducts) : updater,
			}))
		},
		[setRule],
	)

	const selectedKeys = useMemo(
		() => rule.purchasedProducts.map((p) => p.productKey),
		[rule.purchasedProducts],
	)

	const disabledKeys = useMemo(
		() =>
			rule.disabledPurshasedProducts.map((item) => item.productKey),
		[rule.disabledPurshasedProducts],
	)

	if (!_.size(sections)) {
		return (
			<Box>
				{description}
				<Typography variant="body2" color="textSecondary" className="py-4">
					Aucun produit en commun n&apos;est disponible. Merci de sélectionner à nouveau les
					points de vente ciblés.
				</Typography>
			</Box>
		)
	}

	return (
		<Box>
			{description}
			<Box mt={2} display="flex" flexDirection={{ xs: 'column', md: 'row' }} gridGap={16}>
				<Box flex={1} minWidth={0} style={{ maxHeight: '55vh', overflowY: 'auto' }}>
					<Paper
						variant="outlined"
						style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}
					>
						<Box
							style={{
								padding: '11px 16px',
								background: '#fafafa',
								borderBottom: '1px solid rgba(0,0,0,0.08)',
							}}
						>
							<Typography variant="body2" style={{ fontWeight: 500, color: '#424242' }}>
								Liste des offres
							</Typography>
						</Box>
						<Box style={{ padding: '8px 12px' }}>
							{_.size(rule.selectedProducts) > 0 ? (
								<List disablePadding>
									{rule.selectedProducts.map((offer, index) => (
										<ListItem className="px-1" key={`${offer.productKey}-${offer.productOfferedKey}-${index}`}>
											<Box display="flex" alignItems="center" width="100%">
												<Box flex={1}>
													<Typography variant="body2">
														1 {offer.productName} <b>acheté</b> ⇒ 1 {offer.productOfferedName}{' '}
														<b>offert</b>
													</Typography>
												</Box>
												<IconButton
													onClick={() =>
														handleDelete(
															index,
															[
																setSelectedProducts,
																setPurchasedProducts,
																setOfferedProducts,
																setDisabledPurchased,
															],
															setFormValuesAdapter,
														)
													}
													aria-label="supprimer"
												>
													<DeleteOutlinedIcon />
												</IconButton>
											</Box>
										</ListItem>
									))}
								</List>
							) : (
								<Box
									style={{
										textAlign: 'center',
										padding: 24,
										backgroundColor: '#fafafa',
										borderRadius: 10,
										width: '100%',
									}}
								>
									<Typography variant="body1" color="textSecondary">
										Aucune offre configurée
									</Typography>
									<Typography variant="caption" display="block" style={{ marginTop: 8 }}>
										Sélectionnez des produits puis cliquez sur « Ajouter une offre »
									</Typography>
								</Box>
							)}
						</Box>
					</Paper>
				</Box>
				<Box flex={1} minWidth={0}>
					<Box className={classes.scopeTabPanel}>
						<ProductScopeSelector
							embedded
							showRankBadge
							sections={sections}
							selectedKeys={selectedKeys}
							disabledKeys={disabledKeys}
							onToggle={(product, sectionId) =>
								handleProductToggle(
									product,
									sectionId,
									setPurchasedProducts,
									setFormValuesAdapter,
									WITH_BUTTON,
								)
							}
							instructions="Sélectionnez les produits pour constituer une offre"
						/>
					</Box>
					<Box className="pt-2">
						<Button
							onClick={() =>
								handleAddProducts(
									rule.purchasedProducts,
									setSelectedProducts,
									setFormValuesAdapter,
									setDisabledPurchased,
								)
							}
							disabled={_.size(rule.purchasedProducts) === 0}
							style={{ float: 'right', padding: '7px' }}
						>
							Ajouter une offre
						</Button>
					</Box>
				</Box>
			</Box>
		</Box>
	)
}
